import {ScrollService} from "../../common/scroll";
import {ResizeObserverFactory} from "../../common/observation";
import {autoRegister, resolve} from "../../container";
import {HorizontalOverflow, HorizontalOverflowInstaller} from "./horizontalOverflow";
import type {EopScrollButtonLeft, EopScrollButtonRight} from "./scrollButton";

export const HORIZONTAL_SCROLL_ERROR_MARGIN = 8;
export const SCROLL_DURATION = 250;

@autoRegister()
export class ScrollBarFactory {
    private readonly groups = new WeakMap<HTMLElement, ScrollButtonGroup>();

    public constructor(
        private readonly scrollService: ScrollService = resolve(ScrollService),
        private readonly resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private readonly horizontalOverflowInstaller: HorizontalOverflowInstaller = resolve(HorizontalOverflowInstaller)
    ) {
    }

    public registerLeft(parent: HTMLElement, button: EopScrollButtonLeft): ScrollButtonGroup {
        const group = this.groupFor(parent);
        group.setLeft(button);
        return group;
    }

    public registerRight(parent: HTMLElement, button: EopScrollButtonRight): ScrollButtonGroup {
        const group = this.groupFor(parent);
        group.setRight(button);
        return group;
    }

    private groupFor(parent: HTMLElement): ScrollButtonGroup {
        let group = this.groups.get(parent);
        if (!group) {
            group = new ScrollButtonGroup(parent, this.scrollService, this.resizeObserverFactory, this.horizontalOverflowInstaller);
            this.groups.set(parent, group);
        }
        return group;
    }

    public unregisterLeft(parent: HTMLElement, button: EopScrollButtonLeft): void {
        const group = this.groups.get(parent);
        if (group?.removeLeft(button)) {
            this.release(parent, group);
        }
    }

    public unregisterRight(parent: HTMLElement, button: EopScrollButtonRight): void {
        const group = this.groups.get(parent);
        if (group?.removeRight(button)) {
            this.release(parent, group);
        }
    }

    private release(parent: HTMLElement, group: ScrollButtonGroup): void {
        group.disconnect();
        this.groups.delete(parent);
    }
}

export class ScrollButtonGroup {
    private leftButton?: EopScrollButtonLeft;
    private rightButton?: EopScrollButtonRight;

    private readonly scrollableArea: HTMLElement;
    private readonly resizeObserver: ResizeObserver;
    private readonly horizontalOverflow: HorizontalOverflow;

    public constructor(
        private readonly parent: HTMLElement,
        private readonly scrollService: ScrollService,
        resizeObserverFactory: ResizeObserverFactory,
        horizontalOverflowInstaller: HorizontalOverflowInstaller
    ) {
        const area = parent.querySelector<HTMLElement>(":scope > [data-horizontally-scrollable-area]");
        if (!area) {
            throw new Error("Scroll buttons require a sibling [data-horizontally-scrollable-area]");
        }
        this.scrollableArea = area;
        this.scrollableArea.addEventListener("scroll", this.updateVisibility);
        this.scrollableArea.addEventListener("touchend", this.updateVisibility, {passive: true});
        this.horizontalOverflow = horizontalOverflowInstaller.installOn(area);
        this.resizeObserver = resizeObserverFactory.create(this.updateVisibility);
        this.resizeObserver.observe(area);
    }

    public setLeft(button: EopScrollButtonLeft): void {
        if (this.leftButton && this.leftButton !== button) {
            throw new Error("A scroll group supports only one left button");
        }
        this.leftButton = button;
        this.updateVisibility();
    }

    public setRight(button: EopScrollButtonRight): void {
        if (this.rightButton && this.rightButton !== button) {
            throw new Error("A scroll group supports only one right button");
        }
        this.rightButton = button;
        this.updateVisibility();
    }

    public removeLeft(button: EopScrollButtonLeft): boolean {
        if (this.leftButton === button) {
            this.leftButton = undefined;
        }
        this.updateVisibility();
        return !this.leftButton && !this.rightButton;
    }

    public removeRight(button: EopScrollButtonRight): boolean {
        if (this.rightButton === button) {
            this.rightButton = undefined;
        }
        this.updateVisibility();
        return !this.leftButton && !this.rightButton;
    }

    public disconnect(): void {
        this.scrollableArea.removeEventListener("scroll", this.updateVisibility);
        this.scrollableArea.removeEventListener("touchend", this.updateVisibility);
        this.resizeObserver.disconnect();
        this.horizontalOverflow.uninstall();
    }

    public scrollLeft(): void {
        const area = this.scrollableArea;
        const leftEdge = this.innerLeftEdge();
        const visibleWidth = this.visibleWidth();
        const items = Array.from(area.querySelectorAll<HTMLElement>("[data-scrollable-item]"));
        const previousItems = items.filter(item => item.leftOffset() < leftEdge);
        const nearestPreviousItem = previousItems.getLast();
        let previousItem = previousItems.find(item => item.leftOffset() >= leftEdge - visibleWidth);
        if (!previousItem && nearestPreviousItem && nearestPreviousItem.rightOffset() <= this.innerRightEdge()) {
            previousItem = nearestPreviousItem;
        }
        let distance: number;
        if (previousItems.length === 0 || previousItem === previousItems[0]) {
            distance = -area.scrollLeft;
        } else if (previousItem) {
            distance = previousItem.leftOffset() - area.leftOffset() - (this.leftButton?.buttonWidth() ?? 0);
        } else {
            distance = -visibleWidth;
        }
        this.scrollBy(distance);
    }

    public scrollToInitialTarget(): void {
        const selector = this.parent.getAttribute("data-scroll-initial-target");
        const target = selector ? this.scrollableArea.querySelector<HTMLElement>(selector) : null;
        if (target) {
            void this.scrollService.enclosingHorizontalArea(this.scrollableArea)
                .scrollToElementCentered(target, SCROLL_DURATION);
        }
    }

    public scrollRight(): void {
        const leftEdge = this.scrollableArea.leftOffset() + (this.leftButton?.buttonWidth() ?? 0);
        const rightEdge = this.innerRightEdge();
        const items = Array.from(this.scrollableArea.querySelectorAll<HTMLElement>("[data-scrollable-item]"));
        const nextItem = items.find(item => item.rightOffset() > rightEdge && item.leftOffset() > leftEdge + 1);
        let distance: number;
        if (nextItem) {
            distance = nextItem.leftOffset() - leftEdge;
        } else {
            distance = this.scrollableArea.scrollWidth;
        }
        this.scrollBy(distance);
    }

    private innerLeftEdge(): number {
        return this.leftButton?.innerEdge() ?? this.scrollableArea.leftOffset();
    }

    private innerRightEdge(): number {
        return this.rightButton?.innerEdge() ?? this.scrollableArea.rightOffset();
    }

    private visibleWidth(): number {
        return Math.max(1, this.scrollableArea.clientWidth - (this.leftButton?.buttonWidth() ?? 0) - (this.rightButton?.buttonWidth() ?? 0));
    }

    private scrollBy(distance: number): void {
        const area = this.scrollableArea;
        const target = area.scrollLeft + distance;
        void this.scrollService.enclosingHorizontalArea(area).scrollTo(target, SCROLL_DURATION);
    }

    private updateVisibility = (): void => {
        const area = this.scrollableArea;
        const overflow = area.scrollWidth > area.clientWidth + HORIZONTAL_SCROLL_ERROR_MARGIN;
        if (this.leftButton) {
            this.leftButton.active = overflow && area.scrollLeft > HORIZONTAL_SCROLL_ERROR_MARGIN;
        }
        if (this.rightButton) {
            this.rightButton.active = overflow && area.scrollLeft + area.clientWidth + HORIZONTAL_SCROLL_ERROR_MARGIN < area.scrollWidth;
        }
    };
}
